import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';

import {
  AssetDTO,
  CategoryEntity,
  computeDaysOpen,
  EntityRefUtil,
  FILE_MIME_TYPES,
  formatManuallyWithUserTimezone,
  FormElementKind,
  FormType,
  GenerateIssueFilePresignedUploadUrlRequestDTO,
  getMimeTypeFromExtension,
  ISSUE_ATTACHMENT_MAX_FILES,
  ISSUE_FILE_MAX_FILE_SIZE_BYTES,
  ISSUE_PHOTO_MAX_FILES,
  IssueDTO,
  IssueSeverity,
  IssueSource,
  IssueStatus,
  RecordState,
  TagEntity,
} from '@gearbelt/global';
import { Button } from '@gearbelt/ui/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { TooltipArrow, TooltipPortal } from '@radix-ui/react-tooltip';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Controller, FieldErrors, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { match } from 'ts-pattern';
import { z } from 'zod';

import { AWAFormSubmissionDrawer } from '@/awa-pages/[workspaceId]/form/submission-drawer';
import { TagBadges } from '@/components/table-page/columns/tag-badges';
import { AsyncSelect, SELECT_STYLES } from '@/components/ui/async-select';
import { Form } from '@/components/ui/form';
import { Sheet, SheetContent, SheetHeader, SheetPortal, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useToast } from '@/components/ui/toaster';
import { usePermissions } from '@/hooks/use-permissions';
import { useSaveChangesGuard } from '@/hooks/use-save-changes-guard';
import { ArrowLeftIcon } from '@/legacy/components/icons/arrow-left';
import { loadUserOptions, USER_SEARCH_DEBOUNCE_MS } from '@/lib/user-options';
import { cn, initials } from '@/lib/utils';
import { assetKeys, tagKeys } from '@/queries/assets';
import { dashboardKeys } from '@/queries/dashboard';
import { formKeys, getFormByIdQueryOptions } from '@/queries/forms';
import { activityKeys } from '@/queries/inspections';
import { getIssueActionsInfiniteQueryOptions, getIssueByIdQueryOptions, issueKeys } from '@/queries/issues';
import { getAssetLocationQueryOptions } from '@/queries/locations';
import { getUnitsQueryOptions } from '@/queries/units';
import { ApiService } from '@/services/api-v2';
import { AWAApiV2Service } from '@/services/awa-api-v2';
import { useUserStore } from '@/stores/user.store';
import { useIsAWA } from '@/utils/awa';
import { readableFileSize } from '@/utils/file-size';
import { getFileTypeIcon } from '@/utils/file-type';
import { convertFullViewImageUrl, convertImageUrl } from '@/utils/image-url';
import { getIssueSeverityColorClass, ISSUE_SEVERITY_OPTIONS, ISSUE_STATUS_OPTIONS } from '@/utils/issue';
import { getWorkspaceRoute, useWorkspaceNavigate } from '@/utils/routes';
import { shrinkPhoto } from '@/utils/shrink-photo';
import { postToPresignedTarget } from '@/utils/upload';
import { ensureWebSafeImage, HEIC_MIME_TYPES } from '@/utils/web-safe-image';

import { ActivityTimeline } from '../activity/activity-timeline';
import { ArchiveConfirmationDialog } from '../common/archive-confirmation-dialog';
import { DeleteConfirmationDialog } from '../common/delete-confirmation-dialog';
import { ReadOnlyField } from '../common/read-only-field';
import { RestoreConfirmationDialog } from '../common/restore-confirmation-dialog';
import { SaveChangesDialog } from '../common/save-changes-dialog';
import ActivityIcon from '../icons/activity';
import ArchiveIcon from '../icons/archive';
import AttachmentIcon from '../icons/attachment';
import CalendarBlankIcon from '../icons/calendar-blank';
import CloseIcon from '../icons/close';
import DeleteIcon from '../icons/delete';
import { EllipsisHorizontalIcon } from '../icons/ellipsis-horizontal';
import FormChecklistIcon from '../icons/form-checklist';
import PhotoIcon from '../icons/photo';
import PlusIcon from '../icons/plus';
import { ShareIcon } from '../icons/share';
import { UndoIcon } from '../icons/undo';
import WarningIcon from '../icons/warning';
import { WarningTriangleIcon } from '../icons/warning-triangle';
import RepairDrawer from '../repairs/repair-drawer';
import RepairFormDrawer from '../repairs/repair-form-drawer';
import { SingleRecordPdfControls } from '../single-record-pdf-controls';
import { TablePageHeaderActionButton } from '../table-page/table-page-header';
import { Avatar, AvatarFallback, AvatarImage } from '../ui/avatar';
import { Badge } from '../ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Lightbox } from '../ui/lightbox';
import { AsyncCreatableMultiSelect } from '../ui/multi-select';
import { NativeDatePicker } from '../ui/native-date-picker';
import { Select } from '../ui/select';
import { Skeleton } from '../ui/skeleton';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../ui/tooltip';
import { createIssueFormSchema, IssueInputField, IssueTextareaField } from './issue-form';

type UploadingPhoto = {
  id: string;
  file: File;
  preview: string;
  uploading: boolean;
  error?: string;
};
type UploadingAttachment = {
  id: string;
  file: File;
  uploading: boolean;
  error?: string;
};

const ACCEPTED_FILE_TYPES = [...Object.keys(FILE_MIME_TYPES), ...new Set(Object.values(FILE_MIME_TYPES).map((extension) => `.${extension}`))].join(',');
const IMAGE_MIME_ENTRIES = Object.entries(FILE_MIME_TYPES).filter(([mimeType]) => mimeType.startsWith('image/'));
// HEIC is absent from FILE_MIME_TYPES because ensureWebSafeImage transcodes it to
// JPEG before upload — but the picker still has to let the user choose one.
const ACCEPTED_IMAGE_TYPES = [
  ...IMAGE_MIME_ENTRIES.map(([mimeType]) => mimeType),
  ...HEIC_MIME_TYPES,
  ...new Set(IMAGE_MIME_ENTRIES.map(([, extension]) => `.${extension}`)),
  ...[...HEIC_MIME_TYPES].map((mimeType) => `.${mimeType.replace('image/', '')}`),
].join(',');

function toCompletedByOption(issue: IssueDTO | undefined): { label: string; value: string; avatarUrl?: string } | null {
  const actor = issue?.closedByUser ?? issue?.repair?.repairedByUser;
  return actor ? { label: EntityRefUtil.createUserFullNameRef(actor), value: actor.id, avatarUrl: actor.avatarUrl ?? undefined } : null;
}

function partitionBySize(files: File[]) {
  return {
    withinLimit: files.filter((file) => file.size <= ISSUE_FILE_MAX_FILE_SIZE_BYTES),
    oversizedCount: files.filter((file) => file.size > ISSUE_FILE_MAX_FILE_SIZE_BYTES).length,
  };
}

export default function IssueDrawer({
  open: openProp,
  onOpenChange,
  issueId,
  asset: assetProp,
  noTrigger = false,
  callback,
  requirePhotos = false,
  source,
  disableOutsideClickDismiss = false,
  checkFormElementId,
  fieldReference,
  locationId,
  onCloseSuccess,
}: {
  open?: boolean;
  onOpenChange?: (open: boolean, success?: boolean) => void;
  issueId?: string;
  asset?: AssetDTO;
  noTrigger?: boolean;
  requirePhotos?: boolean;
  callback?: (issue: IssueDTO) => void;
  source: IssueSource;
  disableOutsideClickDismiss?: boolean;
  checkFormElementId?: string;
  fieldReference?: string;
  largeButtons?: boolean;
  locationId?: string;
  onCloseSuccess?: (issue: IssueDTO) => void;
}) {
  const userTimezone = useUserStore.getState().user?.timezone;
  const navigate = useWorkspaceNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();
  const { isAWA } = useIsAWA();
  const { canEditIssue } = usePermissions();

  const photoInputRef = useRef<HTMLInputElement>(null);
  const attachmentInputRef = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(openProp ?? false);
  const [photosUploading, setPhotosUploading] = useState<UploadingPhoto[]>([]);
  const [attachmentsUploading, setAttachmentsUploading] = useState<UploadingAttachment[]>([]);
  const [view, setView] = useState<'form' | 'activity'>('form');
  const [showPhoto, setShowPhoto] = useState<string | undefined>(undefined);
  const [isCreateRepairFormOpen, setIsCreateRepairFormOpen] = useState(false);
  const [isViewRepairOpen, setIsViewRepairOpen] = useState(false);
  const [hasRepairFormSuccess, setHasRepairFormSuccess] = useState(false);
  const [hasIssueUpdateSuccess, setHasIssueUpdateSuccess] = useState(false);
  const [isFabMenuOpen, setIsFabMenuOpen] = useState(false);
  const [shouldAutoTriggerSharePdf, setShouldAutoTriggerSharePdf] = useState(false);
  const [isRepairSubmissionDrawerOpen, setIsRepairSubmissionDrawerOpen] = useState(false);
  const [showArchiveConfirm, setShowArchiveConfirm] = useState(false);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const { isConfirmOpen, confirmSafeToLeave, settleConfirm, onConfirmOpenChange } = useSaveChangesGuard(() => form.formState.isDirty);

  async function closeDrawer() {
    if (!(await confirmSafeToLeave())) return;
    handleOpenChange(false, hasRepairFormSuccess || hasIssueUpdateSuccess);
    setHasRepairFormSuccess(false);
    setHasIssueUpdateSuccess(false);
  }

  useEffect(() => {
    setOpen(openProp ?? false);
  }, [openProp]);

  const { data: issueData, isFetching: isFetchingIssue } = useQuery(getIssueByIdQueryOptions({ issueId, isAWA }));

  const issue = issueId ? issueData : undefined;
  // Members can edit any issue (edit-all, per product) but destructive actions
  // — Archive / Delete / Restore — stay scoped to records they authored.
  // Creation mode has no existing issue, so no destructive button renders.
  const canTakeDestructiveActionOnIssue = issue ? canEditIssue(issue.userId) : false;

  const apiService = isAWA ? AWAApiV2Service : ApiService;

  // Fetch full asset to determine repair form ID (FF ON only)
  const issueAssetId = issue?.asset?.id;
  const { data: issueAsset } = useQuery({
    queryKey: assetKeys.detail(issueAssetId, isAWA),
    queryFn: async () => {
      if (!issueAssetId) return null;
      return (await (await apiService.GetAssetById(issueAssetId)).json()).asset;
    },
    enabled: !!issueAssetId,
  });

  const repairFormId = useMemo(() => {
    if (!issueAsset?.forms) return undefined;
    return issueAsset.forms.find((f) => f.type === FormType.RepairForm)?.id;
  }, [issueAsset]);

  // Fetch repair form details for dynamic form name
  const { data: repairFormData } = useQuery({
    ...getFormByIdQueryOptions({ formId: repairFormId, isAWA }),
    enabled: !!repairFormId,
  });
  const repairForm = repairFormId ? repairFormData?.form : undefined;

  // Fetch repair form revision to get UnitReading elements (for "Completed {unit}" fields)
  const repairFormRevisionId = issue?.repair?.formRevisionId;
  const { data: repairFormRevision } = useQuery({
    queryKey: formKeys.detail(repairFormRevisionId, isAWA),
    queryFn: async () => {
      if (!repairFormRevisionId) return null;
      return (await (await apiService.GetFormRevisionById({ formRevisionId: repairFormRevisionId })).json()).formRevision;
    },
    enabled: !!repairFormRevisionId,
  });

  // Fetch units for UnitReading display
  const { data: unitsData } = useQuery({
    ...getUnitsQueryOptions({ isAWA }),
    enabled: !!repairFormRevision,
  });

  // Build "Completed {unit}" data from repair form answers
  const completedUnitFields = useMemo(() => {
    if (!repairFormRevision?.elements || !issue?.repair?.data) return [];
    const unitReadingElements = repairFormRevision.elements.filter((el) => el.kind === FormElementKind.UnitReading);
    return unitReadingElements.map((element) => {
      const unitId = (element.config as { unitId?: string })?.unitId;
      const unit = unitsData?.find((u) => u.id === unitId);
      const answer = issue.repair?.data?.find((a) => a.elementId === element.id);
      return {
        label: unit?.name ?? element.label ?? 'Unit',
        value: answer?.value != null ? Number(answer.value).toLocaleString() : null,
        abbreviation: unit?.abbreviation,
      };
    });
  }, [repairFormRevision, issue?.repair?.data, unitsData]);

  const { data: actionPages, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteQuery(getIssueActionsInfiniteQueryOptions({ issueId, isAWA }));
  const actions = actionPages?.pages.flatMap((page) => page.data) ?? [];

  // Resolve locationId to a full location object for pre-populating the Reported Location field.
  // Uses getAssetLocationQueryOptions (not getLocationByIdQueryOptions) because
  // AWAApiV2Service has no GetLocationById — the asset-location query uses GetLocations
  // with an ID filter, which works in both AWA and web contexts.
  const prePopulateLocationId = !issueId ? locationId : undefined;
  const { data: prePopulatedLocations } = useQuery({
    ...getAssetLocationQueryOptions({ assetLocationId: prePopulateLocationId, isAWA }),
    enabled: !issueId && !!locationId,
  });
  const prePopulatedLocation = prePopulatedLocations?.[0];

  const form = useForm<z.infer<ReturnType<typeof createIssueFormSchema>>>({
    resolver: zodResolver(createIssueFormSchema(requirePhotos)),
    defaultValues: {
      title: issue?.title || '',
      severity: (issue?.severity as IssueSeverity) ?? undefined,
      description: issue?.description || '',
      photos: issue?.photos ?? [],
      asset: issue?.asset
        ? { label: EntityRefUtil.createAssetLabel(issue.asset), value: issue.asset.id, locationId: issue.asset.locationId }
        : assetProp
          ? { label: EntityRefUtil.createAssetLabel(assetProp), value: assetProp.id, locationId: assetProp.locationId }
          : undefined,
      status: (issue?.status as IssueStatus) || IssueStatus.Open,
      attachments: issue?.attachments ?? [],
      tags: issue?.tags?.map(({ tag }) => tag) ?? [],
      category: issue?.category ? { label: issue.category.name, value: issue.category.id } : undefined,
      location: issue?.location
        ? { label: issue.location.name, value: issue.location.id }
        : prePopulatedLocation
          ? { label: prePopulatedLocation.name, value: prePopulatedLocation.id }
          : null,
      closedAt: issue?.closedAt ? new Date(issue.closedAt) : issue?.repair?.repairDate ? new Date(issue.repair.repairDate) : null,
      closedByUser: toCompletedByOption(issue),
    },
  });
  const { setValue, watch, clearErrors } = form;
  // RHF formState uses a Proxy to track subscriptions — a property must be read during render
  // for RHF to know to re-render when it changes. Reading formState properties only inside
  // callbacks (confirmSafeToLeave, onClick handlers, etc.) never registers a subscription,
  // so the value stays stale. Destructuring here makes every isDirty usage below reactive.
  const { isDirty } = form.formState;
  const asset = watch('asset');
  // Completed date + by are editable on any status (daysOpen is status-driven,
  // not closedAt-driven). AWA stays read-only for now — drop the isAWA term to
  // also allow AWA edits.
  const completedFieldsDisabled = isAWA;
  // A closed issue with no repair to fall back to must keep a completed date + by (API-enforced), so
  // both are required here. Keyed on the target status so a future reopen (status → Open) lifts the
  // requirement with no change here.
  const completedFieldsRequired = issue?.status === IssueStatus.Closed && watch('status') === IssueStatus.Closed && !issue?.repair;
  const requiresAssetSelection = !issueId && !asset;
  // An issue created in-context (from an asset or an inspection) is locked to that asset: show it
  // read-only rather than an editable picker. For an existing issue (issueId set) the linked asset
  // comes from the issue itself — callers pass an ambient `assetProp` alongside issueId when viewing,
  // so falling back to it would render the page's current asset as if it were the issue's.
  const displayedAsset = issue?.asset ?? (!issueId ? assetProp : undefined);
  const showReadOnlyAsset = !!displayedAsset;

  useEffect(() => {
    if (!open) return;
    if (issue) {
      form.reset({
        title: issue.title || '',
        severity: (issue.severity as IssueSeverity) ?? IssueSeverity.Critical,
        description: issue.description || '',
        photos: issue.photos || [],
        status: (issue.status as IssueStatus) || IssueStatus.Open,
        attachments: issue.attachments ?? [],
        tags: issue.tags?.map(({ tag }) => tag) || [],
        category: issue.category ? { label: issue.category.name, value: issue.category.id } : undefined,
        asset: issue.asset
          ? { label: EntityRefUtil.createAssetLabel(issue.asset), value: issue.asset.id, locationId: issue.asset.locationId }
          : assetProp
            ? { label: EntityRefUtil.createAssetLabel(assetProp), value: assetProp.id, locationId: assetProp.locationId }
            : undefined,
        fieldReference: issue.fieldReference ?? undefined,
        location: issue.location ? { label: issue.location.name, value: issue.location.id } : null,
        closedAt: issue.closedAt ? new Date(issue.closedAt) : issue.repair?.repairDate ? new Date(issue.repair.repairDate) : null,
        closedByUser: toCompletedByOption(issue),
      });
    } else {
      form.reset({
        title: '',
        severity: undefined,
        description: '',
        photos: [],
        // Include fieldReference in the reset object: the dedicated setValue effect
        // only fires when the prop reference changes, not when `open` changes.
        fieldReference: fieldReference ?? undefined,
        asset: assetProp ? { label: EntityRefUtil.createAssetLabel(assetProp), value: assetProp.id, locationId: assetProp.locationId } : undefined,
        status: IssueStatus.Open,
        attachments: [],
        tags: [],
        category: undefined,
        location: null,
      });
    }
  }, [open, issue, form, assetProp, fieldReference]);

  // Pre-populate location field from the resolved location data.
  // Runs when the drawer opens (open changes) or when async data arrives.
  // Uses setValue instead of form.reset to avoid wiping other fields.
  useEffect(() => {
    if (open && !issueId && prePopulatedLocation) {
      form.setValue('location', { label: prePopulatedLocation.name, value: prePopulatedLocation.id });
    }
  }, [open, issueId, prePopulatedLocation, form]);

  const { mutateAsync: generatePresignedUploadUrl, isPending: isGeneratingPresignedUploadUrl } = useMutation({
    mutationFn: async (dto: GenerateIssueFilePresignedUploadUrlRequestDTO) => {
      if (isAWA) {
        return await (await AWAApiV2Service.GenerateIssueFilePresignedUploadUrl(dto)).json();
      } else {
        return await (await ApiService.GenerateIssueFilePresignedUploadUrl(dto)).json();
      }
    },
  });

  const { mutateAsync: updateIssueState, isPending: isUpdatingIssueState } = useMutation({
    mutationFn: async (state: RecordState) => {
      if (!issue) throw new Error('No issue to update');
      await ApiService.BulkUpdateIssueState({ ids: [issue.id], state });
    },
    onSuccess: async (_, state) => {
      const messages = {
        [RecordState.Active]: 'Issue restored successfully',
        [RecordState.Archived]: 'Issue archived successfully',
        [RecordState.Deleted]: 'Issue deleted successfully',
      };
      toast.success(messages[state]);
      setShowArchiveConfirm(false);
      setShowRestoreConfirm(false);
      setShowDeleteConfirm(false);
      await queryClient.invalidateQueries({ queryKey: issueKeys.all });
      await queryClient.invalidateQueries({ queryKey: tagKeys.all });
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      handleOpenChange(false, true);
    },
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const { mutateAsync: updateIssue, isPending: isUpdatingIssue } = useMutation({
    mutationFn: async (values: z.infer<ReturnType<typeof createIssueFormSchema>>) => {
      if (!issue) throw new Error('No issue to update');
      const { status, category, location, closedAt, closedByUser, ...rest } = values;
      return (
        await ApiService.UpdateIssue(issue.id, {
          ...rest,
          status,
          categoryId: category?.value,
          locationId: location?.value ?? null,
          closedAt: closedAt ? closedAt.toISOString() : null,
          closedByUserId: closedByUser?.value ?? null,
        })
      ).json();
    },
    onSuccess: async (data, variables) => {
      await queryClient.invalidateQueries({ queryKey: issueKeys.all });
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      setHasIssueUpdateSuccess(true);
      callback?.(data.issue);
      toast.success('Issue updated successfully');
      if (issue && issue.status === IssueStatus.Open && variables.status === IssueStatus.Closed) {
        onCloseSuccess?.(data.issue);
      }
    },
    onError: (error) => {
      toast.error(`Error updating issue: ${error.message}`);
    },
  });

  const { mutateAsync: createIssue, isPending: isCreatingIssue } = useMutation({
    mutationFn: async (values: z.infer<ReturnType<typeof createIssueFormSchema>>) => {
      if (isAWA) {
        return await (
          await AWAApiV2Service.CreateIssue({
            assetId: values.asset.value,
            title: values.title,
            fieldReference: values.fieldReference || '',
            severity: values.severity,
            description: values.description || '',
            photos: values.photos,
            source,
            checkFormElementId,
            locationId: values.location?.value ?? locationId,
            tags: values.tags,
            categoryId: values.category?.value,
            attachments: values.attachments,
          })
        ).json();
      } else {
        return await (
          await ApiService.CreateIssue({
            assetId: values.asset.value,
            title: values.title,
            fieldReference: values.fieldReference || '',
            severity: values.severity,
            description: values.description || '',
            photos: values.photos,
            source,
            checkFormElementId,
            locationId: values.location?.value ?? asset?.locationId ?? undefined,
            tags: values.tags,
            categoryId: values.category?.value,
            attachments: values.attachments,
          })
        ).json();
      }
    },
    onSuccess: async (data) => {
      await queryClient.invalidateQueries({ queryKey: issueKeys.all });
      await queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
      if (isAWA) {
        await queryClient.invalidateQueries({ queryKey: activityKeys.all });
      }
      callback?.(data.issue);
      handleOpenChange(false, true);
      form.reset();
      toast.success('Issue added successfully');
    },
    onError: (error) => {
      toast.error(`Error adding issue: ${error.message}`);
    },
  });

  async function uploadFile(file: File): Promise<string> {
    const { upload, fileUrl } = await generatePresignedUploadUrl({
      filename: `${Date.now()}-${file.name}`,
      contentType: file.type,
      assetId: asset?.value,
      issueId: issue?.id,
    });

    await postToPresignedTarget(upload, file);

    return fileUrl;
  }

  async function handleFileUpload<T extends { id: string; file: File; uploading: boolean; error?: string }>(
    files: ArrayLike<File>,
    options: {
      formFieldName: 'photos' | 'attachments';
      setUploadingState: React.Dispatch<React.SetStateAction<T[]>>;
      createUploadingItem: (file: File, id: string) => T;
      cleanupItem?: (item: T) => void;
      appendToForm: (file: File, uploadedUrl: string) => void;
    },
  ) {
    const fileArray = Array.from(files);
    const newUploadingItems = fileArray.map((file) => {
      const id = `${Date.now()}-${Math.random()}`;
      return options.createUploadingItem(file, id);
    });

    options.setUploadingState((prev) => [...prev, ...newUploadingItems]);

    for (const uploadingItem of newUploadingItems) {
      try {
        const uploadedUrl = await uploadFile(uploadingItem.file);

        options.appendToForm(uploadingItem.file, uploadedUrl);

        options.setUploadingState((prev) => prev.filter((item) => item.id !== uploadingItem.id));

        if (options.cleanupItem) {
          options.cleanupItem(uploadingItem);
        }
        clearErrors(options.formFieldName);
      } catch (error) {
        options.setUploadingState((prev) =>
          prev.map((item) => (item.id === uploadingItem.id ? { ...item, uploading: false, error: (error as Error).message } : item)),
        );

        toast.error(`Failed to upload ${uploadingItem.file.name}: ${(error as Error).message}`);
      }
    }
  }

  const handlePhotoUpload = async (files: FileList) => {
    const photosUploaded = (form.getValues('photos') ?? []).length;
    const photosUploadingCount = photosUploading.length;
    const remainingSlots = ISSUE_PHOTO_MAX_FILES - photosUploaded - photosUploadingCount;
    if (remainingSlots <= 0) return;

    const fileArray = Array.from(files).slice(0, remainingSlots);
    if (fileArray.length < files.length) {
      toast.error(`Maximum of ${ISSUE_PHOTO_MAX_FILES} photos allowed.`);
    }

    const conversionResults = await Promise.allSettled(fileArray.map(async (file) => shrinkPhoto(await ensureWebSafeImage(file))));

    const safeFiles: File[] = [];
    conversionResults.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        safeFiles.push(result.value);
      } else {
        toast.error(`Failed to process ${fileArray[index].name}`);
      }
    });

    const { withinLimit: photosWithinLimit, oversizedCount: oversizedPhotoCount } = partitionBySize(safeFiles);
    if (oversizedPhotoCount > 0) {
      toast.error(`Maximum file size is ${readableFileSize(ISSUE_FILE_MAX_FILE_SIZE_BYTES)}.`);
    }

    if (photosWithinLimit.length === 0) return;

    return handleFileUpload<UploadingPhoto>(photosWithinLimit, {
      formFieldName: 'photos',
      setUploadingState: setPhotosUploading,
      createUploadingItem: (file, id): UploadingPhoto => ({
        id,
        file,
        preview: URL.createObjectURL(file),
        uploading: true,
      }),
      cleanupItem: (item) => URL.revokeObjectURL(item.preview),
      appendToForm: (_file, uploadedUrl) => {
        const currentValues = form.getValues('photos') || [];
        form.setValue('photos', [...currentValues, uploadedUrl], { shouldDirty: true });
      },
    });
  };

  const handleAttachmentUpload = (files: FileList) => {
    const attachmentsUploaded = (form.getValues('attachments') ?? []).length;
    const attachmentsUploadingCount = attachmentsUploading.length;
    const remainingSlots = ISSUE_ATTACHMENT_MAX_FILES - attachmentsUploaded - attachmentsUploadingCount;
    if (remainingSlots <= 0) return;

    const fileArray = Array.from(files).slice(0, remainingSlots);
    if (fileArray.length < files.length) {
      toast.error(`Maximum of ${ISSUE_ATTACHMENT_MAX_FILES} attachments allowed.`);
    }

    const { withinLimit: attachmentsWithinLimit, oversizedCount: oversizedAttachmentCount } = partitionBySize(fileArray);
    if (oversizedAttachmentCount > 0) {
      toast.error(`Maximum file size is ${readableFileSize(ISSUE_FILE_MAX_FILE_SIZE_BYTES)}.`);
    }

    if (attachmentsWithinLimit.length === 0) return;

    return handleFileUpload<UploadingAttachment>(attachmentsWithinLimit, {
      formFieldName: 'attachments',
      setUploadingState: setAttachmentsUploading,
      createUploadingItem: (file, id): UploadingAttachment => ({
        id,
        file,
        uploading: true,
      }),
      appendToForm: (file, uploadedUrl) => {
        const currentValues = form.getValues('attachments') || [];
        form.setValue('attachments', [...currentValues, { name: file.name, url: uploadedUrl }], { shouldDirty: true });
      },
    });
  };

  function resetDrawerState() {
    form.reset();
    // Clean up uploading photos and their preview URLs
    photosUploading.forEach((photo) => URL.revokeObjectURL(photo.preview));
    setPhotosUploading([]);
    setAttachmentsUploading([]);
    setHasRepairFormSuccess(false);
    setHasIssueUpdateSuccess(false);
    setView('form');
  }

  function handleOpenChange(open: boolean, success?: boolean) {
    if (!open && (isCreateRepairFormOpen || isViewRepairOpen)) return;
    onOpenChange?.(open, success ?? ((hasRepairFormSuccess || hasIssueUpdateSuccess) && !open));
    setOpen(open);
    if (!open) {
      resetDrawerState();
    }
  }

  async function handleSubmit(values: z.infer<ReturnType<typeof createIssueFormSchema>>): Promise<boolean> {
    if (photosUploading.some((photo) => photo.uploading)) {
      toast.error('Please wait for all photos to finish uploading before submitting');
      return false;
    }
    if (attachmentsUploading.some((attachment) => attachment.uploading)) {
      toast.error('Please wait for all attachments to finish uploading before submitting');
      return false;
    }

    if (issue) {
      // A closed issue with no repair to fall back to must keep a completed date + by (API-enforced).
      if (completedFieldsRequired && (!values.closedAt || !values.closedByUser)) {
        if (!values.closedAt) {
          form.setError('closedAt', { type: 'manual', message: 'Required' });
        }
        if (!values.closedByUser) {
          form.setError('closedByUser', { type: 'manual', message: 'Required' });
        }
        return false;
      }
      await updateIssue(values);
    } else {
      await createIssue(values);
    }
    return true;
  }

  function handleError(errors: FieldErrors<z.infer<ReturnType<typeof createIssueFormSchema>>>) {
    const fieldNames = Object.keys(errors);
    if (fieldNames.length > 0) {
      const firstFieldWithError = document.getElementById(fieldNames[0]);
      if (firstFieldWithError) {
        firstFieldWithError.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  const issueSeverityOptions = useMemo(() => {
    return ISSUE_SEVERITY_OPTIONS.map((option) => {
      const issueSeverityColorClass = getIssueSeverityColorClass(option.value);
      const issueSeverityIcon =
        option.value === IssueSeverity.Critical ? (
          <WarningIcon size={20} className="text-default-red" />
        ) : (
          <div className="grid size-4 place-items-center">
            <div className={cn('mx-[3px] h-[15px] w-[15px] rounded-full border-[3px]', issueSeverityColorClass)} />
          </div>
        );

      return {
        ...option,
        label: (
          <div className="flex items-center gap-1 leading-none">
            {issueSeverityIcon}
            {option.label}
          </div>
        ),
      };
    });
  }, []);

  const issueStatusOptions = useMemo(() => {
    return ISSUE_STATUS_OPTIONS.map((option) => {
      return {
        ...option,
        label: (
          <Badge
            className="text-xs"
            variant={match(option.value)
              .with(IssueStatus.Open, () => 'grey' as const)
              .with(IssueStatus.Closed, () => 'success' as const)
              .exhaustive()}>
            {option.label}
          </Badge>
        ),
      };
    });
  }, []);

  const statusBadgeVariant = match(issue?.status as IssueStatus | undefined)
    .with(IssueStatus.Open, () => 'grey' as const)
    .with(IssueStatus.Closed, () => 'success' as const)
    .otherwise(() => 'grey' as const);

  const statusText = match(issue?.status as IssueStatus | undefined)
    .with(IssueStatus.Open, () => 'Open')
    .with(IssueStatus.Closed, () => 'Closed')
    .otherwise(() => issue?.status ?? '-');

  useEffect(() => {
    if (assetProp) {
      setValue('asset', { label: EntityRefUtil.createAssetLabel(assetProp), value: assetProp.id, locationId: assetProp.locationId });
    }
  }, [assetProp, setValue]);

  useEffect(() => {
    if (fieldReference) {
      setValue('fieldReference', fieldReference);
    }
  }, [fieldReference, setValue]);

  const daysOpen = useMemo(() => {
    return issue ? computeDaysOpen({ createdAt: issue.createdAt, closedAt: issue.closedAt, status: issue.status }) : 0;
  }, [issue]);

  return (
    <>
      <Sheet
        open={open}
        onOpenChange={(newOpen) => {
          if (!newOpen && (isCreateRepairFormOpen || isViewRepairOpen)) return;
          if (newOpen) {
            handleOpenChange(true);
            return;
          }
          closeDrawer();
        }}>
        {!noTrigger && (
          <SheetTrigger asChild>
            <TablePageHeaderActionButton icon={<PlusIcon />} label="Create Issue" variant="success" kind="primary" onClick={() => setOpen(true)}>
              Create Issue
            </TablePageHeaderActionButton>
          </SheetTrigger>
        )}
        <SheetPortal>
          <SheetContent
            onInteractOutside={(e) => {
              if (attachmentsUploading.length > 0 || photosUploading.length > 0) {
                toast.error('Please wait for all attachments and photos to finish uploading before closing');
                e.preventDefault();
                return;
              }
              if (disableOutsideClickDismiss || form.formState.isSubmitting) {
                e.preventDefault();
              }
            }}
            onOpenAutoFocus={(e) => {
              if (isAWA) {
                e.preventDefault();
              }
            }}
            hideCloseIcon
            side={'right'}
            className={cn(isAWA ? 'mx-auto h-full w-full focus:outline-none sm:max-w-[800px]' : 'focus:outline-none', isFabMenuOpen && 'border-l-0')}>
            <Form {...form}>
              <form
                className="flex h-full flex-1 flex-col"
                onSubmit={(e) => {
                  e.stopPropagation();
                  form.handleSubmit(handleSubmit, handleError)(e);
                }}>
                <SheetHeader className="border-grey-3 h-[64px] flex-row items-center justify-between space-y-0 border-b bg-white">
                  {isAWA ? (
                    <>
                      <div className="flex items-center gap-2 py-1">
                        <button
                          className="-m-2 p-2"
                          type="button"
                          onClick={async () => {
                            if (attachmentsUploading.length > 0 || photosUploading.length > 0) {
                              toast.error('Please wait for all attachments and photos to finish uploading before closing');
                              return;
                            }
                            if (!(await confirmSafeToLeave())) return;
                            handleOpenChange(false);
                          }}
                          disabled={form.formState.isSubmitting}>
                          <ArrowLeftIcon className="size-6" />
                        </button>
                        <SheetTitle className="text-xl">
                          {isFetchingIssue && !isDirty ? 'Loading issue details...' : issue ? 'Issue record' : 'Create Issue'}
                        </SheetTitle>
                      </div>

                      {!issueId && (
                        <div className="flex items-center gap-4">
                          <Button
                            variant="link"
                            className="text-default-green text-xl font-medium hover:no-underline"
                            size="default"
                            type="submit"
                            disabled={form.formState.isSubmitting || !asset}
                            loading={isCreatingIssue || isUpdatingIssue}>
                            Submit
                          </Button>
                        </div>
                      )}
                    </>
                  ) : (
                    <>
                      <SheetTitle>{isFetchingIssue && !isDirty ? 'Loading issue details...' : issue ? 'Issue record' : 'Create Issue'}</SheetTitle>
                      <div className="flex items-center gap-2">
                        {!isAWA && !!issue && <SingleRecordPdfControls recordType="issue" recordId={issue.id} documentUrl={undefined} />}
                        {!!issue && issue.state === RecordState.Active && canTakeDestructiveActionOnIssue && (
                          <TooltipProvider>
                            <Tooltip delayDuration={0}>
                              <TooltipTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  type="button"
                                  aria-label="Archive"
                                  onClick={() => setShowArchiveConfirm(true)}
                                  disabled={isUpdatingIssueState}>
                                  <ArchiveIcon className="h-4 w-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipPortal>
                                <TooltipContent className="bg-default-text pointer-events-none border-none text-xs text-white">
                                  <TooltipArrow className="fill-default-text" />
                                  Archive
                                </TooltipContent>
                              </TooltipPortal>
                            </Tooltip>
                          </TooltipProvider>
                        )}
                        {!!issue && issue.state === RecordState.Archived && canTakeDestructiveActionOnIssue && (
                          <>
                            <TooltipProvider>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    type="button"
                                    aria-label="Delete"
                                    className="text-default-red"
                                    onClick={() => setShowDeleteConfirm(true)}
                                    disabled={isUpdatingIssueState}>
                                    <DeleteIcon className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipPortal>
                                  <TooltipContent className="bg-default-text pointer-events-none border-none text-xs text-white">
                                    <TooltipArrow className="fill-default-text" />
                                    Delete
                                  </TooltipContent>
                                </TooltipPortal>
                              </Tooltip>
                            </TooltipProvider>
                            <TooltipProvider>
                              <Tooltip delayDuration={0}>
                                <TooltipTrigger asChild>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    type="button"
                                    aria-label="Restore"
                                    onClick={() => setShowRestoreConfirm(true)}
                                    disabled={isUpdatingIssueState}>
                                    <UndoIcon className="h-4 w-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipPortal>
                                  <TooltipContent className="bg-default-text pointer-events-none border-none text-xs text-white">
                                    <TooltipArrow className="fill-default-text" />
                                    Restore
                                  </TooltipContent>
                                </TooltipPortal>
                              </Tooltip>
                            </TooltipProvider>
                          </>
                        )}
                        {issue && (
                          <Button
                            variant="grey"
                            size="default"
                            onClick={() => setView((prev) => (prev === 'activity' ? 'form' : 'activity'))}
                            icon={<ActivityIcon />}
                            type="button">
                            {view === 'activity' ? 'Hide Activity' : 'Show Activity'}
                          </Button>
                        )}
                        <Button
                          variant="success"
                          size="default"
                          type="submit"
                          disabled={form.formState.isSubmitting || !asset || !isDirty}
                          loading={isCreatingIssue || isUpdatingIssue}>
                          Save
                        </Button>
                        <button
                          type="button"
                          className="data-[state=open]:bg-default-blue cursor-pointer rounded-sm opacity-70 transition-opacity outline-none hover:opacity-100 disabled:pointer-events-none"
                          disabled={form.formState.isSubmitting}
                          onClick={async () => {
                            if (attachmentsUploading.length > 0 || photosUploading.length > 0) {
                              toast.error('Please wait for all attachments and photos to finish uploading before closing');
                              return;
                            }
                            if (!(await confirmSafeToLeave())) return;
                            handleOpenChange(false);
                          }}>
                          <CloseIcon className="h-4 w-4" />
                          <span className="sr-only">Close</span>
                        </button>
                      </div>
                    </>
                  )}
                </SheetHeader>

                {isFetchingIssue && !isDirty ? (
                  <div className="flex flex-col gap-2 px-4 pt-4">
                    <div className="flex items-center justify-between gap-2">
                      <div className="flex flex-col gap-1">
                        {[...Array(2)].map((_, i) => (
                          <Skeleton key={i} className={cn('h-4 w-28', i === 1 && 'w-32')} />
                        ))}
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col items-end gap-1">
                          {[...Array(2)].map((_, i) => (
                            <Skeleton key={i} className={cn('h-4 w-28', i === 1 && 'w-32')} />
                          ))}
                        </div>
                        <Skeleton className="size-7 rounded-full" />
                      </div>
                    </div>

                    <div className="border-grey-2 animate-pulse border-b-4"></div>

                    <div className="flex flex-col gap-4 px-2 py-3">
                      {[...Array(5)].map((_, i) => (
                        <Skeleton key={i} className={cn('h-12 w-full rounded px-6', i === 2 && 'h-36')} />
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="flex h-full flex-1 flex-col overflow-y-auto">
                    {view === 'form' && (
                      <>
                        {!!issue && (
                          <div className="flex flex-col gap-1 px-4 pt-4">
                            <div className="flex items-center justify-between gap-2 text-sm">
                              <div className="flex flex-col gap-[2px]">
                                <div>
                                  <span className="font-medium">ID: </span> {EntityRefUtil.createIssueRef(issue.number)}
                                </div>
                                <div className="flex items-center gap-1">
                                  <span className="font-medium">Days Open: </span>
                                  <span className="">
                                    <TooltipProvider>
                                      <Tooltip delayDuration={0}>
                                        <TooltipTrigger asChild>
                                          <div className="bg-default-red flex items-center gap-1 rounded-full px-1.5 py-0.5 text-sm text-white">
                                            <span>{daysOpen}</span>
                                          </div>
                                        </TooltipTrigger>
                                        <TooltipPortal>
                                          <TooltipContent className="bg-default-text pointer-events-none border-none text-sm text-white">
                                            <TooltipArrow className="fill-default-text" />
                                            This issue has been open for {daysOpen} days.
                                          </TooltipContent>
                                        </TooltipPortal>
                                      </Tooltip>
                                    </TooltipProvider>
                                  </span>
                                </div>
                              </div>

                              <div className="flex items-center gap-2 text-right">
                                <div className="flex flex-col gap-[2px]">
                                  <div className="line-clamp-1 font-medium">{EntityRefUtil.createUserFullNameRef(issue.user, '-')}</div>
                                  <div className="text-secondary-text">
                                    {isAWA
                                      ? dayjs(issue.createdAt).format('MMM DD, YYYY hh:mm a')
                                      : formatManuallyWithUserTimezone(new Date(issue.createdAt), 'MMM DD, YYYY hh:mm a', userTimezone)}
                                  </div>
                                </div>
                                <Avatar>
                                  {issue.user && issue.user.avatarUrl ? (
                                    <AvatarImage src={issue.user.avatarUrl} />
                                  ) : (
                                    <AvatarFallback>{initials(EntityRefUtil.createUserFullNameRef(issue.user, '-'))}</AvatarFallback>
                                  )}
                                </Avatar>
                              </div>
                            </div>

                            <div className="border-default-blue border-b-4" />
                          </div>
                        )}

                        <div className="scrollbar scrollbar-track-transparent scrollbar-thumb-grey-3 flex flex-1 flex-col gap-2 overflow-y-auto px-6 pt-4 pb-6">
                          {!issueId && !assetProp && (
                            <Controller
                              control={form.control}
                              name="asset"
                              render={({ field: { value, onChange } }) => (
                                <div id="asset-select">
                                  <Label required>Asset</Label>
                                  <AsyncSelect<{ label: string; value: string; locationId: string | null }>
                                    cacheOptions
                                    defaultOptions
                                    value={value}
                                    onChange={(val) => onChange(val ?? undefined)}
                                    loadOptions={async (input: string) => {
                                      const { data } = await (
                                        await (isAWA ? AWAApiV2Service : ApiService).GetAssets({
                                          limit: 100,
                                          filters: {
                                            state: RecordState.Active,
                                            search: input,
                                          },
                                        })
                                      ).json();

                                      return (
                                        data?.map((asset) => ({
                                          value: asset.id,
                                          label: EntityRefUtil.createAssetLabel(asset),
                                          locationId: asset.locationId,
                                        })) ?? []
                                      );
                                    }}
                                    classNames={SELECT_STYLES()}
                                    className={cn('w-full')}
                                    blurInputOnSelect
                                    isClearable
                                    placeholder="Select Asset..."
                                    aria-label="Asset"
                                  />
                                </div>
                              )}
                            />
                          )}

                          {showReadOnlyAsset && displayedAsset && (
                            <div className="flex flex-col gap-1">
                              <Label>Asset</Label>
                              <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                                {isAWA ? (
                                  <div className="text-sm">{EntityRefUtil.createAssetLabel(displayedAsset)}</div>
                                ) : (
                                  <Link className="text-default-blue text-sm underline" to={getWorkspaceRoute(`/assets/${displayedAsset.id}`)}>
                                    {EntityRefUtil.createAssetLabel(displayedAsset)}
                                  </Link>
                                )}
                              </div>
                            </div>
                          )}

                          {issue &&
                            (isAWA ? (
                              <ReadOnlyField label="Status">
                                <Badge size="sm" variant={statusBadgeVariant}>
                                  {statusText}
                                </Badge>
                              </ReadOnlyField>
                            ) : (
                              <Controller
                                control={form.control}
                                name="status"
                                render={({ field: { value, onChange } }) => (
                                  <div id="status-select">
                                    <Label required>Status</Label>
                                    <Select<{ label: ReactNode; value: IssueStatus }>
                                      value={issueStatusOptions.find((option) => option.value === value) ?? undefined}
                                      onChange={(val) => onChange(val?.value)}
                                      options={issueStatusOptions}
                                      className="w-full"
                                      blurInputOnSelect
                                    />
                                  </div>
                                )}
                              />
                            ))}

                          <IssueInputField
                            label="Title"
                            name="title"
                            control={form.control}
                            placeholder="Enter issue title..."
                            required
                            disabled={(isAWA && !!issue) || requiresAssetSelection}
                          />

                          <IssueTextareaField
                            label="Description"
                            name="description"
                            control={form.control}
                            placeholder="Enter issue details..."
                            readonly={(isAWA && !!issue) || requiresAssetSelection}
                          />

                          <Controller
                            control={form.control}
                            name="severity"
                            render={({ field: { value, onChange }, fieldState: { error } }) => (
                              <div id="severity-select" className="flex flex-col gap-1">
                                <Label required>Severity</Label>
                                <Select<{ label: ReactNode; value: IssueSeverity }>
                                  value={issueSeverityOptions.find((option) => option.value === value) ?? undefined}
                                  onChange={(val) => onChange(val?.value)}
                                  options={issueSeverityOptions}
                                  classNames={SELECT_STYLES(!!error)}
                                  styles={{
                                    control: (provided, state) => ({
                                      ...provided,
                                      backgroundColor: state.isDisabled ? 'rgb(var(--grey-1))' : provided.backgroundColor,
                                      opacity: '1',
                                    }),
                                  }}
                                  className={cn('w-full')}
                                  blurInputOnSelect
                                  placeholder="Select severity..."
                                  aria-label="Severity"
                                  isDisabled={(isAWA && !!issue) || requiresAssetSelection}
                                />
                                {error && <div className="text-default-red text-sm">{error.message}</div>}
                              </div>
                            )}
                          />

                          {issue && (
                            <div className="flex flex-col gap-1">
                              <Label>Field Reference</Label>
                              <Input disabled value={fieldReference || issue.fieldReference || '-'} />
                            </div>
                          )}

                          <Controller
                            control={form.control}
                            name="photos"
                            render={({ field: { value, onChange }, fieldState: { error } }) => {
                              const currentCount = (value?.length ?? 0) + photosUploading.length;
                              const isPhotosLimitReached = currentCount >= ISSUE_PHOTO_MAX_FILES;

                              return (
                                <div className="flex flex-col gap-1" id="photos">
                                  <Label required={requirePhotos}>Photos</Label>

                                  <div className="flex flex-wrap items-center gap-2">
                                    {/* Uploaded photos */}
                                    {value?.length > 0 &&
                                      value?.map((photo, index) => (
                                        <PhotoItem
                                          key={index}
                                          src={convertImageUrl(photo, { width: 200, height: 200 })}
                                          isUploading={false}
                                          hasError={false}
                                          onDelete={() => {
                                            const newPhotos = [...(value as string[])];
                                            newPhotos.splice(index, 1);
                                            onChange(newPhotos);
                                          }}
                                          onClick={() => setShowPhoto(photo)}
                                        />
                                      ))}

                                    {/* Uploading photos */}
                                    {photosUploading.map((uploadingPhoto) => (
                                      <PhotoItem
                                        key={uploadingPhoto.id}
                                        src={uploadingPhoto.preview}
                                        isUploading={uploadingPhoto.uploading}
                                        hasError={!!uploadingPhoto.error}
                                        onDelete={() => {
                                          setPhotosUploading((prev) => prev.filter((p) => p.id !== uploadingPhoto.id));
                                          URL.revokeObjectURL(uploadingPhoto.preview);
                                        }}
                                      />
                                    ))}
                                  </div>

                                  {!!issue && value?.length === 0 && <div className="text-secondary-text text-sm">No photos uploaded</div>}
                                  {error && <div className="text-default-red text-sm">{error.message}</div>}

                                  <div className="mt-1 flex items-center gap-2">
                                    <Button
                                      className="w-fit leading-none"
                                      icon={<PhotoIcon />}
                                      type="button"
                                      disabled={isGeneratingPresignedUploadUrl || (isAWA && !!issue) || requiresAssetSelection || isPhotosLimitReached}
                                      onClick={() => {
                                        photoInputRef.current?.click();
                                      }}>
                                      Add {currentCount > 0 ? 'More ' : ''}Photos
                                    </Button>
                                    {currentCount > 0 && (
                                      <span className="text-secondary-text text-sm">
                                        {currentCount}/{ISSUE_PHOTO_MAX_FILES} photos
                                      </span>
                                    )}
                                  </div>
                                  {isPhotosLimitReached && (
                                    <p className="text-secondary-text mt-1 text-sm">Maximum number of photos reached ({ISSUE_PHOTO_MAX_FILES})</p>
                                  )}

                                  <input
                                    ref={photoInputRef}
                                    type="file"
                                    accept={ACCEPTED_IMAGE_TYPES}
                                    multiple
                                    className="hidden"
                                    disabled={isPhotosLimitReached}
                                    onChange={(e) => {
                                      const files = e.target.files;
                                      if (files && files.length > 0) {
                                        handlePhotoUpload(files);
                                      }
                                      // Reset the input so the same file can be selected again
                                      e.target.value = '';
                                    }}
                                  />
                                </div>
                              );
                            }}
                          />

                          <div className="flex flex-col gap-1">
                            <Label>Work Order</Label>
                            <div className="bg-grey-1 ring-grey-3 flex min-h-[41px] flex-wrap items-center gap-1 rounded p-2 ring-2 ring-inset">
                              {issue?.workOrder ? (
                                isAWA ? (
                                  <span className="text-sm">{EntityRefUtil.createWorkOrderRef(issue.workOrder.number)}</span>
                                ) : (
                                  <button
                                    type="button"
                                    className="text-default-blue text-sm underline"
                                    onClick={async () => {
                                      if (!(await confirmSafeToLeave())) return;
                                      if (issue?.workOrder) {
                                        try {
                                          const workOrderData = (await (await ApiService.GetWorkOrderById({ workOrderId: issue.workOrder.id })).json())
                                            .workOrder;
                                          navigate('/work-orders', { state: { workOrder: workOrderData } });
                                        } catch (error) {
                                          console.error('Error fetching work order:', error);
                                          toast.error('Failed to load work order');
                                        }
                                      }
                                    }}>
                                    {EntityRefUtil.createWorkOrderRef(issue.workOrder.number)}
                                  </button>
                                )
                              ) : (
                                <div className="text-sm">-</div>
                              )}
                            </div>

                            {!issue?.workOrder && (
                              <TooltipProvider>
                                <Tooltip delayDuration={0}>
                                  <TooltipTrigger asChild>
                                    <span className="mt-1 w-fit">
                                      <Button
                                        className="leading-none"
                                        icon={<CalendarBlankIcon />}
                                        type="button"
                                        disabled={!issue?.id || isAWA}
                                        onClick={async () => {
                                          if (!(await confirmSafeToLeave())) return;
                                          navigate('/work-orders', { state: { issue } });
                                        }}>
                                        Create Work Order
                                      </Button>
                                    </span>
                                  </TooltipTrigger>
                                  {!issue?.id && (
                                    <TooltipPortal>
                                      <TooltipContent className="bg-default-text pointer-events-none border-none text-xs text-white">
                                        <TooltipArrow className="fill-default-text" />
                                        Save the Issue first - the new Work Order will be linked to it.
                                      </TooltipContent>
                                    </TooltipPortal>
                                  )}
                                </Tooltip>
                              </TooltipProvider>
                            )}
                          </div>

                          {issue && repairForm && (
                            <ReadOnlyField label="Repair Form">
                              <span className="text-sm">{repairForm.name}</span>
                            </ReadOnlyField>
                          )}

                          {issue && (
                            <div className="flex flex-col gap-1">
                              <ReadOnlyField label="Repair Form Record">
                                {issue.repair?.state === RecordState.Deleted ? (
                                  <div className="flex items-center gap-2">
                                    <span className="text-sm">{EntityRefUtil.createRepairRef(issue.repair.number)}</span>
                                    <Badge variant="destructive" size="sm">
                                      Deleted
                                    </Badge>
                                  </div>
                                ) : issue.repair ? (
                                  <button
                                    type="button"
                                    className="text-default-blue text-sm underline"
                                    onClick={async () => {
                                      if (!(await confirmSafeToLeave())) return;
                                      setOpen(false);
                                      setIsViewRepairOpen(true);
                                    }}>
                                    {EntityRefUtil.createRepairRef(issue.repair.number)}
                                  </button>
                                ) : (
                                  <span className="text-sm">-</span>
                                )}
                              </ReadOnlyField>

                              {repairFormId && (
                                <Button
                                  className="mt-1 w-fit"
                                  variant="default"
                                  icon={<AttachmentIcon />}
                                  type="button"
                                  disabled={!!issue.repair || issue.status === IssueStatus.Closed || watch('status') === IssueStatus.Closed}
                                  onClick={async () => {
                                    if (!(await confirmSafeToLeave())) return;
                                    setOpen(false);
                                    setIsCreateRepairFormOpen(true);
                                  }}>
                                  Create Repair Form Record
                                </Button>
                              )}
                            </div>
                          )}

                          {!(isAWA && !issue) && (
                            <>
                              <div className="flex flex-col gap-1" id="tags-select">
                                <Label>Issue Tags</Label>
                                <Controller
                                  control={form.control}
                                  name="tags"
                                  render={({ field: { value, onChange } }) => (
                                    <AsyncCreatableMultiSelect<{ label: ReactNode; value: string }>
                                      defaultOptions
                                      loadOptions={async (input: string) => {
                                        const { data } = await (
                                          await (isAWA ? AWAApiV2Service : ApiService).GetTags({
                                            limit: 100,
                                            filters: {
                                              search: input,
                                              fields: { entity: TagEntity.Issue },
                                            },
                                          })
                                        ).json();

                                        return (
                                          data?.map((tag) => ({
                                            value: tag.tag,
                                            label: tag.tag,
                                          })) ?? []
                                        );
                                      }}
                                      value={value?.map((tag) => ({ label: tag, value: tag }))}
                                      onChange={(val) => onChange(val?.map((v) => v.value))}
                                      chipVariant="orange"
                                      className={cn('w-full')}
                                      placeholder="Select Tags..."
                                      isDisabled={(isAWA && !!issue) || requiresAssetSelection}
                                    />
                                  )}
                                />
                              </div>

                              {issue?.asset && (
                                <ReadOnlyField label="Asset Tags">
                                  <TagBadges tags={issue.asset.tags} />
                                </ReadOnlyField>
                              )}

                              <div className="flex flex-col gap-1" id="categories-select">
                                <Label>Category</Label>
                                <Controller
                                  control={form.control}
                                  name="category"
                                  render={({ field: { value, onChange } }) => (
                                    <AsyncSelect<{ label: string; value: string }>
                                      cacheOptions
                                      defaultOptions
                                      loadOptions={async (input: string) => {
                                        const { data } = await (
                                          await (isAWA ? AWAApiV2Service : ApiService).GetCategories({
                                            limit: 100,
                                            filters: {
                                              search: input,
                                              fields: { entity: CategoryEntity.Issue },
                                            },
                                          })
                                        ).json();

                                        return (
                                          data?.map((category) => ({
                                            value: category.id,
                                            label: category.name,
                                          })) ?? []
                                        );
                                      }}
                                      value={value}
                                      onChange={(val) => {
                                        onChange(val);
                                      }}
                                      classNames={SELECT_STYLES()}
                                      className={cn('w-full')}
                                      styles={{
                                        control: (provided, state) => ({
                                          ...provided,
                                          backgroundColor: state.isDisabled ? 'rgb(var(--grey-1))' : provided.backgroundColor,
                                          opacity: '1',
                                        }),
                                      }}
                                      placeholder="Select Category..."
                                      isDisabled={(isAWA && !!issue) || requiresAssetSelection}
                                    />
                                  )}
                                />
                              </div>
                            </>
                          )}

                          <Controller
                            control={form.control}
                            name="location"
                            render={({ field: { value, onChange } }) => (
                              <div className="flex break-inside-avoid flex-col gap-1">
                                <Label>Reported Location</Label>
                                <AsyncSelect<{ label: string; value: string }>
                                  value={value ?? null}
                                  cacheOptions
                                  defaultOptions
                                  loadOptions={async (input: string) => {
                                    const { data } = await (
                                      await (isAWA ? AWAApiV2Service : ApiService).GetLocations({
                                        limit: 100,
                                        filters: { state: RecordState.Active, search: input },
                                      })
                                    ).json();
                                    return data?.map((location) => ({ value: location.id, label: location.name })) ?? [];
                                  }}
                                  onChange={(val) => onChange(val ?? null)}
                                  classNames={SELECT_STYLES()}
                                  className={cn('w-full')}
                                  styles={{
                                    control: (provided, state) => ({
                                      ...provided,
                                      backgroundColor: state.isDisabled ? 'rgb(var(--grey-1))' : provided.backgroundColor,
                                      opacity: '1',
                                    }),
                                  }}
                                  isClearable
                                  isDisabled={(isAWA && !!issue) || requiresAssetSelection}
                                  placeholder="Select Location..."
                                />
                              </div>
                            )}
                          />

                          {issue && (
                            <>
                              <Controller
                                control={form.control}
                                name="closedAt"
                                render={({ field: { value, onChange }, fieldState: { error } }) => (
                                  <div className="flex break-inside-avoid flex-col gap-1">
                                    <Label required={completedFieldsRequired}>Completed Date</Label>
                                    <NativeDatePicker
                                      value={value ?? null}
                                      onChange={(date) => {
                                        onChange(date);
                                        clearErrors('closedAt');
                                      }}
                                      disabled={completedFieldsDisabled}
                                      error={!!error}
                                      placeholder="Select completed date"
                                    />
                                    {error && <div className="text-default-red text-sm">{error.message}</div>}
                                  </div>
                                )}
                              />

                              {completedUnitFields.map((field) => (
                                <ReadOnlyField key={field.label} label={`Completed ${field.label}`}>
                                  <span className="text-sm">
                                    {field.value != null ? `${field.value}${field.abbreviation ? ` ${field.abbreviation}` : ''}` : '-'}
                                  </span>
                                </ReadOnlyField>
                              ))}

                              <Controller
                                control={form.control}
                                name="closedByUser"
                                render={({ field: { value, onChange }, fieldState: { error } }) => (
                                  <div className="flex break-inside-avoid flex-col gap-1">
                                    <Label required={completedFieldsRequired}>Completed By</Label>
                                    <AsyncSelect<{ label: string; value: string; avatarUrl?: string }>
                                      defaultOptions={!isAWA}
                                      classNames={SELECT_STYLES(!!error)}
                                      value={value ?? null}
                                      onChange={(option) => {
                                        onChange(option ?? null);
                                        clearErrors('closedByUser');
                                      }}
                                      debounceMs={USER_SEARCH_DEBOUNCE_MS}
                                      loadOptions={(input) => loadUserOptions(input, 'all', () => toast.error('Failed to load users'))}
                                      formatOptionLabel={(option) => (
                                        <div className="flex items-center gap-2">
                                          <Avatar className="h-5 w-5">
                                            {option.avatarUrl ? (
                                              <AvatarImage src={option.avatarUrl} alt={option.label} />
                                            ) : (
                                              <AvatarFallback className="text-[10px]">{initials(option.label)}</AvatarFallback>
                                            )}
                                          </Avatar>
                                          <span>{option.label}</span>
                                        </div>
                                      )}
                                      isClearable
                                      isDisabled={completedFieldsDisabled}
                                      placeholder="Select completed by..."
                                    />
                                    {error && <div className="text-default-red text-sm">{error.message}</div>}
                                  </div>
                                )}
                              />
                            </>
                          )}

                          {!(isAWA && !issue) && (
                            <Controller
                              control={form.control}
                              name="attachments"
                              render={({ field: { value, onChange } }) => {
                                const currentCount = (value?.length ?? 0) + attachmentsUploading.length;
                                const isAttachmentsLimitReached = currentCount >= ISSUE_ATTACHMENT_MAX_FILES;

                                return (
                                  <div className="flex break-inside-avoid flex-col gap-2" id="attachments">
                                    <Label>Attachments</Label>

                                    {/* Uploaded attachments */}
                                    {value?.length > 0 &&
                                      value?.map((attachment, index) => (
                                        <FileItem
                                          key={index}
                                          url={attachment.url}
                                          filename={attachment.name}
                                          isUploading={false}
                                          hasError={false}
                                          badge={
                                            attachment.state === RecordState.Archived ? (
                                              <Badge variant="orange" className="mr-2 shrink-0">
                                                Archived
                                              </Badge>
                                            ) : undefined
                                          }
                                          onDelete={() => {
                                            const newAttachments = [...value];
                                            newAttachments.splice(index, 1);
                                            onChange(newAttachments);
                                          }}
                                        />
                                      ))}

                                    {/* Uploading attachments */}
                                    {attachmentsUploading.map((uploadingAttachment) => (
                                      <FileItem
                                        key={uploadingAttachment.id}
                                        filename={uploadingAttachment.file.name}
                                        isUploading={uploadingAttachment.uploading}
                                        hasError={!!uploadingAttachment.error}
                                        onDelete={() => setAttachmentsUploading((prev) => prev.filter((a) => a.id !== uploadingAttachment.id))}
                                      />
                                    ))}

                                    {!!issue && value?.length === 0 && <div className="text-secondary-text text-sm">No attachments uploaded</div>}

                                    <div className="mt-1 flex items-center gap-2">
                                      <Button
                                        className="w-fit leading-none"
                                        icon={<AttachmentIcon />}
                                        type="button"
                                        disabled={isGeneratingPresignedUploadUrl || (isAWA && !!issue) || requiresAssetSelection || isAttachmentsLimitReached}
                                        onClick={() => {
                                          attachmentInputRef.current?.click();
                                        }}>
                                        Add {currentCount > 0 ? 'More ' : ''}Attachment{currentCount === 1 ? '' : 's'}
                                      </Button>
                                      {currentCount > 0 && (
                                        <span className="text-secondary-text text-sm">
                                          {currentCount}/{ISSUE_ATTACHMENT_MAX_FILES} attachments
                                        </span>
                                      )}
                                    </div>
                                    {isAttachmentsLimitReached && (
                                      <p className="text-secondary-text mt-1 text-sm">Maximum number of attachments reached ({ISSUE_ATTACHMENT_MAX_FILES})</p>
                                    )}

                                    <input
                                      ref={attachmentInputRef}
                                      type="file"
                                      accept={ACCEPTED_FILE_TYPES}
                                      multiple
                                      className="hidden"
                                      disabled={isAttachmentsLimitReached}
                                      onChange={(e) => {
                                        const files = e.target.files;
                                        if (files && files.length > 0) {
                                          handleAttachmentUpload(files);
                                        }
                                        // Reset the input so the same file can be selected again
                                        e.target.value = '';
                                      }}
                                    />
                                  </div>
                                );
                              }}
                            />
                          )}
                        </div>
                      </>
                    )}

                    {view === 'activity' && (
                      <div className="flex flex-1 flex-col gap-2 p-4">
                        <div>Activity</div>

                        <div className="border-b" />

                        <ActivityTimeline actions={actions} hasNextPage={hasNextPage} isFetchingNextPage={isFetchingNextPage} fetchNextPage={fetchNextPage} />
                      </div>
                    )}
                  </div>
                )}
              </form>
            </Form>

            {/* FAB menu overlay */}
            {isAWA && repairFormId && isFabMenuOpen && (
              <div
                className="data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 fixed inset-0 z-50 bg-black/80"
                onClick={() => setIsFabMenuOpen(false)}
              />
            )}

            {/* FAB menu for AWA actions */}
            {isAWA && repairFormId && (
              <div className="absolute right-4 bottom-4 z-50">
                <DropdownMenu open={isFabMenuOpen} onOpenChange={setIsFabMenuOpen}>
                  <DropdownMenuTrigger asChild>
                    <Button variant="default" size="icon" className="bg-default-blue hover:bg-default-dark-blue h-[65px] w-[65px] rounded-full shadow-md">
                      {isFabMenuOpen ? <CloseIcon size={30} className="text-white" /> : <EllipsisHorizontalIcon size={30} className="text-white" />}
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" side="top" className="mb-5 flex flex-col gap-[20px] border-0 bg-transparent p-0 shadow-none">
                    {/* Share as PDF button */}
                    <DropdownMenuItem
                      asChild
                      disabled={!issue?.repair}
                      onClick={() => {
                        if (issue?.repair) {
                          setIsFabMenuOpen(false);
                          setOpen(false);
                          setShouldAutoTriggerSharePdf(true);
                          setIsViewRepairOpen(true);
                        }
                      }}
                      className="cursor-pointer p-0 hover:bg-transparent focus:bg-transparent data-[disabled]:opacity-100 data-[highlighted]:bg-transparent">
                      <div className="flex w-full items-center justify-end gap-2.5">
                        <div className="text-default-text rounded bg-white px-2.5 py-1 text-base font-medium shadow-md">Share as PDF</div>
                        <div
                          className={cn(
                            'bg-default-blue flex h-[65px] w-[65px] items-center justify-center rounded-full shadow-md',
                            !issue?.repair && 'opacity-50',
                          )}>
                          <ShareIcon size={30} className="text-white" />
                        </div>
                      </div>
                    </DropdownMenuItem>
                    {/* Create Repair Record button */}
                    <DropdownMenuItem
                      asChild
                      disabled={issue?.status === IssueStatus.Closed || watch('status') === IssueStatus.Closed || !!issue?.repair}
                      onClick={() => {
                        if (issue?.status !== IssueStatus.Closed && watch('status') !== IssueStatus.Closed && !issue?.repair) {
                          setIsFabMenuOpen(false);
                          setOpen(false);
                          setIsCreateRepairFormOpen(true);
                        }
                      }}
                      className="cursor-pointer p-0 hover:bg-transparent focus:bg-transparent data-[disabled]:opacity-100 data-[highlighted]:bg-transparent">
                      <div className="flex w-full items-center justify-end gap-2.5">
                        <div className="text-default-text rounded bg-white px-2.5 py-1 text-base font-medium shadow-md">
                          Create {repairForm?.name ?? 'Repair Record'}
                        </div>
                        <div
                          className={cn(
                            'bg-default-blue flex h-[65px] w-[65px] items-center justify-center rounded-full shadow-md',
                            (issue?.status === IssueStatus.Closed || watch('status') === IssueStatus.Closed || !!issue?.repair) && 'opacity-50',
                          )}>
                          <FormChecklistIcon size={30} className="text-white" />
                        </div>
                      </div>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            )}
          </SheetContent>
        </SheetPortal>
      </Sheet>

      {showPhoto && <Lightbox photos={[convertFullViewImageUrl(showPhoto)]} onHide={() => setShowPhoto(undefined)} />}

      {issue && issue.asset && repairFormId && (
        <RepairFormDrawer
          formId={repairFormId}
          assetId={issue.asset.id}
          issue={issue}
          open={isCreateRepairFormOpen}
          onOpenChange={(open, success) => {
            setIsCreateRepairFormOpen(open);
            if (success) {
              setHasRepairFormSuccess(true);
              queryClient.invalidateQueries({ queryKey: issueKeys.all });
              queryClient.invalidateQueries({ queryKey: dashboardKeys.all });
              // Show submission drawer for AWA
              if (isAWA) {
                setIsRepairSubmissionDrawerOpen(true);
              }
            }
            if (!open && !(isAWA && success)) {
              setOpen(true);
            }
          }}
        />
      )}

      {isAWA && (
        <AWAFormSubmissionDrawer
          open={isRepairSubmissionDrawerOpen}
          onOpenChange={(open) => {
            setIsRepairSubmissionDrawerOpen(open);
            if (!open) {
              handleOpenChange(true, hasRepairFormSuccess);
            }
          }}
          title="Repair form record submitted"
          description="Go to the home page to continue working or view the completed issue"
          recordLabel="View issue"
          recordIcon={<WarningTriangleIcon size={20} />}
        />
      )}

      {issue?.repair && (
        <RepairDrawer
          repairId={issue.repair.id}
          open={isViewRepairOpen}
          noTrigger
          autoTriggerSharePdf={shouldAutoTriggerSharePdf}
          onOpenChange={(open) => {
            setIsViewRepairOpen(open);
            if (!open) {
              setShouldAutoTriggerSharePdf(false);
              setOpen(true);
            }
          }}
        />
      )}

      <ArchiveConfirmationDialog
        open={showArchiveConfirm}
        onOpenChange={setShowArchiveConfirm}
        callback={() => updateIssueState(RecordState.Archived)}
        loading={isUpdatingIssueState}
        title="Archive issue?"
        description="Are you sure you want to archive this issue? You can access or restore archived issues via the archived tab."
      />

      <RestoreConfirmationDialog
        open={showRestoreConfirm}
        onOpenChange={setShowRestoreConfirm}
        callback={() => updateIssueState(RecordState.Active)}
        loading={isUpdatingIssueState}
        title="Restore issue?"
        description="Are you sure you want to restore this issue? It will be moved back to your active issues."
      />

      <DeleteConfirmationDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        callback={() => updateIssueState(RecordState.Deleted)}
        loading={isUpdatingIssueState}
        title="Delete issue?"
        description="This action is permanent and cannot be undone. Are you sure you want to delete this issue?"
      />
      <SaveChangesDialog
        open={isConfirmOpen}
        onOpenChange={onConfirmOpenChange}
        saveCallback={form.handleSubmit(async (values) => {
          try {
            if (await handleSubmit(values)) {
              settleConfirm(true);
              handleOpenChange(false, true);
            }
          } catch {
            // mutation onError already shows a toast; leave the dialog open for retry
          }
        }, handleError)}
        discardCallback={() => {
          form.reset();
          settleConfirm(true);
        }}
        loading={form.formState.isSubmitting}
        title="Save changes?"
        description="Do you want to save your changes before leaving?"
      />
    </>
  );
}

function PhotoItem({
  src,
  isUploading = false,
  hasError = false,
  onDelete,
  onClick,
}: {
  src: string;
  isUploading?: boolean;
  hasError?: boolean;
  onDelete?: () => void;
  onClick?: () => void;
}) {
  const { isAWA } = useIsAWA();

  return (
    <div className="relative w-fit">
      <img src={src} alt="" className="size-[84px] cursor-pointer rounded object-cover" onClick={onClick} />
      {isUploading && (
        <div className="bg-opacity-50 absolute inset-0 flex items-center justify-center rounded bg-black">
          <div className="size-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
        </div>
      )}
      {hasError && (
        <div className="bg-opacity-75 absolute inset-0 flex items-center justify-center rounded bg-red-500">
          <WarningIcon size={20} className="text-white" />
        </div>
      )}
      {!isAWA && (
        <Button
          size="icon"
          type="button"
          variant="destructive"
          aria-label="Remove photo"
          className="hover:bg-grey-1 absolute -top-2 -right-2 size-4 rounded-full bg-white outline-none"
          onClick={(e) => {
            e.stopPropagation();
            onDelete?.();
          }}>
          <CloseIcon className="text-default-red size-4" />
        </Button>
      )}
    </div>
  );
}

function FileItem({
  filename,
  isUploading = false,
  hasError = false,
  onDelete,
  icon,
  url,
  badge,
}: {
  filename: string;
  isUploading?: boolean;
  hasError?: boolean;
  onDelete?: () => void;
  icon?: ReactNode;
  url?: string;
  badge?: ReactNode;
}) {
  const { isAWA } = useIsAWA();
  const Element = url ? 'a' : 'div';

  return (
    <Element
      href={url || undefined}
      target={url ? '_blank' : undefined}
      rel={url ? 'noreferrer' : undefined}
      className="border-grey-3 flex items-center justify-between border-b p-2">
      <div className="flex min-w-0 flex-1 items-center gap-3">
        <div className="relative flex-shrink-0">
          {icon || getFileTypeIcon(getMimeTypeFromExtension(filename.split('.').pop() ?? '') ?? '')}
          {isUploading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="size-3 animate-spin rounded-full border border-gray-400 border-t-transparent" />
            </div>
          )}
        </div>
        <span className="line-clamp-1 min-w-0 flex-1 text-sm font-medium">{filename}</span>
        {isUploading && <span className="flex-shrink-0 text-xs text-gray-500">Uploading...</span>}
        {hasError && <span className="flex-shrink-0 text-xs text-red-500">Failed</span>}
      </div>
      {badge}
      {!isAWA && (
        <Button
          type="button"
          variant="outline"
          className="text-default-red flex items-center gap-1 border-none outline-none"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onDelete?.();
          }}
          disabled={isUploading}>
          <DeleteIcon className="h-4 w-4" />
          Delete
        </Button>
      )}
    </Element>
  );
}
